import {
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import type {
  ConfirmImportPayload,
  ImportFormatResponse,
  ImportJobResponse,
  ImportMappingDTO,
  ImportOptionsResponse,
  ImportProbeResponse,
  ImportSessionResponse,
} from "../types/purchaseRequestImport.type";

const baseUrl = "/api/purchase-requests/imports";

/** How often to ask again while the file is being read or the requests written. */
const POLL_MS = 2000;

export const getImportErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const body = error.response?.data as
      | { message?: string; error?: string }
      | string
      | undefined;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") {
      return body.message || body.error || error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "";
};

/**
 * Asks whether we have read a file like this one before, without starting anything.
 *
 * Sent the moment a file is chosen. If the company already has a format for these columns, the
 * screen has nothing left to ask: the mapping, the value decisions and the description are all
 * saved, and the upload goes straight to the preview. Only an unrecognised file is worth
 * interrupting somebody about, which is the whole reason this call exists.
 */
export const useProbeImport = (
  onSuccess?: (probe: ImportProbeResponse) => void,
  onError?: (error: unknown) => void
) =>
  useMutation<ImportProbeResponse, unknown, { file: File; sheetName?: string }>({
    mutationFn: async ({ file, sheetName }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (sheetName) formData.append("sheetName", sheetName);
      const { data } = await axios.post<ImportProbeResponse>(`${baseUrl}/probe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess,
    onError,
  });

/**
 * Sends the file and what the buyer said about it.
 *
 * Comes back before the mapping exists — the backend answers as soon as the file is stored, so
 * that nothing is held open across a model call. The session is then polled.
 */
export const useStartImport = (
  onSuccess?: (session: ImportSessionResponse) => void,
  onError?: (error: unknown) => void
) =>
  useMutation<
    ImportSessionResponse,
    unknown,
    { file: File; description?: string; sheetName?: string }
  >({
    mutationFn: async ({ file, description, sheetName }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (description) formData.append("description", description);
      if (sheetName) formData.append("sheetName", sheetName);
      const { data } = await axios.post<ImportSessionResponse>(baseUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess,
    onError,
  });

/**
 * Sends a Google Sheets link instead of a file.
 *
 * The same flow from here on: the sheet becomes the same grid an .xlsx does, so it gets the same
 * mapping, the same preview, the same duplicate check and the same saved formats.
 */
export const useStartSheetImport = (
  onSuccess?: (session: ImportSessionResponse) => void,
  onError?: (error: unknown) => void
) =>
  useMutation<
    ImportSessionResponse,
    unknown,
    { spreadsheetUrl: string; sheetName?: string; description?: string }
  >({
    mutationFn: async (payload) => {
      const { data } = await axios.post<ImportSessionResponse>(
        `${baseUrl}/google-sheets`,
        payload
      );
      return data;
    },
    onSuccess,
    onError,
  });

/**
 * The session, its mapping and its preview.
 *
 * Polls only while there is something to wait for. Once the file has been read the answer does
 * not change on its own, so asking again would be noise.
 *
 * Keeps polling with the tab in the background. React Query stops interval refetching on a hidden
 * tab by default, which is right for a list nobody is looking at and wrong here: working out how
 * to read a file takes a minute or two, and a buyer who switches tabs while they wait should come
 * back to the answer rather than to the spinner they left.
 */
export const useImportSession = (sessionId: string | null, enabled = true) =>
  useQuery<ImportSessionResponse>({
    queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_SESSION, sessionId],
    queryFn: async () => {
      const { data } = await axios.get<ImportSessionResponse>(`${baseUrl}/${sessionId}`);
      return data;
    },
    enabled: enabled && !!sessionId,
    refetchInterval: (query: Query<ImportSessionResponse, Error>) =>
      query.state.data?.status === "ANALYZING" ? POLL_MS : false,
    refetchIntervalInBackground: true,
  });

/** Replaces the mapping. The response carries the file re-read through it. */
export const useReplaceImportMapping = (
  sessionId: string | null,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<ImportSessionResponse, unknown, ImportMappingDTO>({
    mutationFn: async (mapping) => {
      const { data } = await axios.put<ImportSessionResponse>(
        `${baseUrl}/${sessionId}/mapping`,
        mapping
      );
      return data;
    },
    onSuccess: (session) =>
      queryClient.setQueryData(
        [queryKeys.PURCHASE_REQUEST_IMPORT_SESSION, sessionId],
        session
      ),
    onError,
  });
};

/**
 * Records what the buyer decided some values mean.
 *
 * Keyed by column and then by raw value, which is one decision per distinct value rather than one
 * per row.
 */
export const useResolveImportValues = (
  sessionId: string | null,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ImportSessionResponse,
    unknown,
    Record<string, Record<string, string>>
  >({
    mutationFn: async (resolutions) => {
      const { data } = await axios.put<ImportSessionResponse>(
        `${baseUrl}/${sessionId}/resolutions`,
        { resolutions }
      );
      return data;
    },
    onSuccess: (session) =>
      queryClient.setQueryData(
        [queryKeys.PURCHASE_REQUEST_IMPORT_SESSION, sessionId],
        session
      ),
    onError,
  });
};

/** Uses a saved format for this file instead of whatever the session had. */
export const useApplyImportFormat = (
  sessionId: string | null,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<ImportSessionResponse, unknown, string>({
    mutationFn: async (formatId) => {
      const { data } = await axios.put<ImportSessionResponse>(
        `${baseUrl}/${sessionId}/format/${formatId}`,
        {}
      );
      return data;
    },
    onSuccess: (session) =>
      queryClient.setQueryData(
        [queryKeys.PURCHASE_REQUEST_IMPORT_SESSION, sessionId],
        session
      ),
    onError,
  });
};

/** Creates the requests. Answers with the job to watch, not with the requests. */
export const useConfirmImport = (
  sessionId: string | null,
  onSuccess?: (job: ImportJobResponse) => void,
  onError?: (error: unknown) => void
) =>
  useMutation<ImportJobResponse, unknown, ConfirmImportPayload>({
    mutationFn: async (payload) => {
      const { data } = await axios.post<ImportJobResponse>(
        `${baseUrl}/${sessionId}/confirm`,
        payload
      );
      return data;
    },
    onSuccess,
    onError,
  });

/** Abandons the import and deletes the uploaded file. */
export const useDiscardImport = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) =>
  useMutation<void, unknown, string>({
    mutationFn: async (sessionId) => {
      await axios.delete(`${baseUrl}/${sessionId}`);
    },
    onSuccess,
    onError,
  });

/**
 * How the import is getting on. Stops asking once it is done either way.
 *
 * Also polls in the background, for the same reason: writing several hundred requests is not
 * something to sit and watch.
 */
export const useImportJob = (jobId: string | null) =>
  useQuery<ImportJobResponse>({
    queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_JOB, jobId],
    queryFn: async () => {
      const { data } = await axios.get<ImportJobResponse>(`${baseUrl}/jobs/${jobId}`);
      return data;
    },
    enabled: !!jobId,
    refetchInterval: (query: Query<ImportJobResponse, Error>) =>
      query.state.data?.status === "PROCESSING" ? POLL_MS : false,
    refetchIntervalInBackground: true,
  });

/**
 * What the buyer can choose from when a value could not be matched.
 *
 * One call rather than three, and the whole category list rather than only the categories already
 * in use — which is what somebody mapping a hundred new category names actually needs.
 */
export const useImportOptions = (enabled = true) =>
  useQuery<ImportOptionsResponse>({
    queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_OPTIONS],
    queryFn: async () => {
      const { data } = await axios.get<ImportOptionsResponse>(`${baseUrl}/options`);
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useImportFormats = (enabled = true) =>
  useQuery<ImportFormatResponse[]>({
    queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_FORMATS],
    queryFn: async () => {
      const { data } = await axios.get<ImportFormatResponse[]>(`${baseUrl}/formats`);
      return data;
    },
    enabled,
  });

/** Remembers the current mapping under a name, so the next file of this shape asks nothing. */
export const useSaveImportFormat = (
  sessionId: string | null,
  onSuccess?: (format: ImportFormatResponse) => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<ImportFormatResponse, unknown, string>({
    mutationFn: async (name) => {
      const { data } = await axios.post<ImportFormatResponse>(
        `${baseUrl}/${sessionId}/format`,
        { name }
      );
      return data;
    },
    onSuccess: (format) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_FORMATS],
      });
      onSuccess?.(format);
    },
    onError,
  });
};

export const useDeleteImportFormat = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (formatId) => {
      await axios.delete(`${baseUrl}/formats/${formatId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_IMPORT_FORMATS],
      });
      onSuccess?.();
    },
  });
};
